import { BAOCustomElement } from '@bao/custom-element'
import { sectionRenderer } from '@bao/section-rendering'
import { getElement, translateString } from '@bao/utils'

const SWISH_READY_EVENT = 'swish-ready'
const SWISH_READY_TIMEOUT = 10000

const DRAWER_KEY = 'wishlist'
const TOAST_DRAWER_KEY = 'wishlist-toast'

const PRODUCT_CARD_SECTION = 'product-card'
const PRODUCT_CARD_SELECTOR = 'product-card'

const SAVED_FROM_CART_LIST = 'saved-from-cart'

const VIEWS = {
  HOME: 'home',
  SAVES: 'saves',
  LISTS: 'lists',
  LIST: 'list',
}

const SWISH_EVENTS = [
  'item-create',
  'item-update',
  'item-delete',
  'item-lists-update',
  'list-create',
  'list-update',
  'list-delete',
]

const EXTERNAL_SELECTORS = {
  TRIGGER: '[data-swish-wishlist-external-el="trigger"]',
}

const SELECTORS = {
  ADD_TO_LIST: '[data-swish-wishlist-add-to-list]',
  REMOVE: '[data-swish-wishlist-remove]',
  REMOVE_CONFIRM: '[data-swish-wishlist-remove-confirm]',
  REMOVE_CANCEL: '[data-swish-wishlist-remove-cancel]',
  ITEM: '[data-swish-wishlist-item]',
  CARD_HEART:
    '.prd-Card_MediaButtons [data-swish-wishlist-external-el="trigger"]',
}

const ITEM_ACTIONS = {
  ADD_TO_LIST: 'add-to-list',
}

/*
| Where an item's X removes it from: just the open list, the whole wishlist
| (which Swish also strips from every list), or the separate "saved from
| cart" smart list, whose items aren't regular saves.
*/
const REMOVE_SCOPES = {
  LIST: 'list',
  WISHLIST: 'wishlist',
  SAVED_FROM_CART: SAVED_FROM_CART_LIST,
}

/*
| Swish fires change events for removals we make ourselves. The drawer
| already updates in place for those, so skip the full re-render briefly.
*/
const OWN_CHANGE_GRACE_PERIOD = 1500

const TOAST_EVENTS = {
  ADD_TO_LIST: 'wishlist-toast:add-to-list',
  CREATE_LIST: 'wishlist-toast:create-list',
  SAVE_LISTS: 'wishlist-toast:save-lists',
  SAVE_CREATE: 'wishlist-toast:save-create',
  VIEW: 'wishlist-toast:view',
  RETURN_TO_WISHLIST: 'wishlist-toast:return-to-wishlist',
  COMPLETE: 'wishlist-toast:complete',
}

let swishPromise

class SwishWishlist extends BAOCustomElement() {
  static get requiredElements () {
    return [
      'body',
      'homeHeader',
      'subviewHeader',
      'home',
      'savedFromCartSection',
      'savedFromCart',
      'homeSaves',
      'homeLists',
      'homeListsViewAll',
      'savedCount',
      'saves',
      'savesContent',
      'lists',
      'listsContent',
      'list',
      'listTitle',
      'listCount',
      'listContent',
      'listTemplate',
      'removeIcon',
    ]
  }

  constructor () {
    super()

    this.activeView = VIEWS.HOME
    this.activeList = null
    this.pendingDrawerView = null
    this.returnDrawerView = VIEWS.HOME

    this.wishlistTriggerStates = new Map()
    this.productCardCache = new Map()
    this.ignoreSwishChangesUntil = 0

    this.unsubscribeSwishEvents = null
    this.disposeCountEffect = null
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.listeners.add(
      document,
      'click',
      this.handleWishlistTriggerClick.bind(this),
    )
    this.listeners.add(
      document,
      'drawer-changed',
      this.handleDrawerChanged.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.ADD_TO_LIST,
      this.handleToastAddToList.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.CREATE_LIST,
      this.handleToastCreateList.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.SAVE_LISTS,
      this.handleToastSaveLists.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.SAVE_CREATE,
      this.handleToastSaveCreate.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.VIEW,
      this.handleToastView.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.COMPLETE,
      this.handleToastComplete.bind(this),
    )

    this.listeners.add(
      document,
      TOAST_EVENTS.RETURN_TO_WISHLIST,
      this.handleToastReturnToWishlist.bind(this),
    )

    this.initialise()
  }

  disconnectedCallback () {
    this.unsubscribeSwishEvents?.()
    this.disposeCountEffect?.()

    this.wishlistTriggerStates.forEach(({ state, disposeEffect }) => {
      disposeEffect()
      state.dispose()
    })

    this.wishlistTriggerStates.clear()

    super.disconnectedCallback()
  }

  async initialise () {
    try {
      this.siteDrawers = getElement('site-drawers')
      this.swish = await this.getSwish()

      this.setupWishlistTriggers()
      this.setupWishlistCount()
      this.setupSwishEventListeners()

      this.dataset.ready = 'true'

      if (this.drawerIsOpen) {
        await this.showView(VIEWS.HOME)
      }
    } catch (error) {
      console.error('Swish Wishlist: Initialisation failed.', error)

      this.dataset.error = 'true'
      this.setLoading(false)
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Drawer
  |--------------------------------------------------------------------------
  |
  */
  handleDrawerChanged () {
    if (this.swish) {
      this.setupWishlistTriggers(
        document.querySelectorAll(EXTERNAL_SELECTORS.TRIGGER),
      )
    }

    if (!this.drawerIsOpen) {
      this.setLoading(false)

      return
    }

    if (!this.swish) {
      this.setLoading(true)

      return
    }

    const view = this.pendingDrawerView || VIEWS.HOME

    this.pendingDrawerView = null
    this.showView(view)
  }

  get drawerIsOpen () {
    return window.drawers?.activeDrawerKey === DRAWER_KEY
  }

  /*
  |--------------------------------------------------------------------------
  | Views
  |--------------------------------------------------------------------------
  |
  */
  handleClick (event) {
    const addToListTrigger = event.target.closest(SELECTORS.ADD_TO_LIST)

    if (addToListTrigger) {
      event.preventDefault()

      this.handleAddToListClick(addToListTrigger)

      return
    }

    const removeConfirmTrigger = event.target.closest(SELECTORS.REMOVE_CONFIRM)

    if (removeConfirmTrigger) {
      event.preventDefault()

      this.handleRemoveConfirm(removeConfirmTrigger)

      return
    }

    const removeCancelTrigger = event.target.closest(SELECTORS.REMOVE_CANCEL)

    if (removeCancelTrigger) {
      event.preventDefault()

      this.hideRemoveConfirm(removeCancelTrigger.closest(SELECTORS.ITEM), {
        restoreFocus: true,
      })

      return
    }

    const removeTrigger = event.target.closest(SELECTORS.REMOVE)

    if (removeTrigger) {
      event.preventDefault()
      event.stopPropagation()

      this.handleRemoveClick(removeTrigger)

      return
    }

    const backTrigger = event.target.closest('[data-swish-wishlist-back]')

    if (backTrigger) {
      this.handleBack()

      return
    }

    const createListTrigger = event.target.closest(
      '[data-swish-wishlist-create-list]',
    )

    if (createListTrigger) {
      this.handleCreateList()

      return
    }

    const listActionTrigger = event.target.closest('[data-swish-list-action]')

    if (listActionTrigger) {
      event.preventDefault()

      this.handleListAction(listActionTrigger)

      return
    }

    const listTrigger = event.target.closest('[data-swish-wishlist-list-id]')

    if (listTrigger) {
      event.preventDefault()

      this.handleListClick(listTrigger)

      return
    }

    const viewTrigger = event.target.closest('[data-swish-wishlist-view]')

    if (!viewTrigger) return

    this.showView(viewTrigger.dataset.swishWishlistView)
  }

  handleCreateList () {
    const toast = this.toast

    if (!toast) return

    this.returnDrawerView = this.activeView

    toast.showCreate({
      standalone: true,
    })

    this.siteDrawers.open(TOAST_DRAWER_KEY)
  }

  handleBack () {
    if (this.activeView === VIEWS.LIST) {
      this.showView(VIEWS.LISTS)

      return
    }

    this.showView(VIEWS.HOME)
  }

  handleListClick (trigger) {
    this.activeList = {
      id: trigger.dataset.swishWishlistListId,
      name: trigger.dataset.swishWishlistListName,
      itemCount: Number(trigger.dataset.swishWishlistListCount || 0),
    }

    this.updateListView()
    this.showView(VIEWS.LIST)
  }

  async showView (view) {
    if (!this.swish || !Object.values(VIEWS).includes(view)) return

    const viewChanged = view !== this.activeView

    this.activeView = view

    this.updateViewHeaders()
    this.updateViewPanels()
    this.updateViewTriggers()
    this.setLoading(true)

    try {
      await this.loadView(view)

      if (viewChanged) {
        this.scrollViewToTop(view)
      }
    } catch (error) {
      console.error(`Swish Wishlist: Failed to load the ${view} view.`, error)

      this.renderViewError(view)
    } finally {
      this.setLoading(false)
    }
  }

  scrollViewToTop (view) {
    const panel = this.els[view]?.element

    if (!panel) return

    panel.scrollIntoView({
      behavior: 'auto',
      block: 'start',
    })
  }

  updateViewHeaders () {
    const isHome = this.activeView === VIEWS.HOME

    this.els.homeHeader.element.hidden = !isHome
    this.els.subviewHeader.element.hidden = isHome
  }

  updateViewPanels () {
    const panels = {
      [VIEWS.HOME]: this.els.home.element,
      [VIEWS.SAVES]: this.els.saves.element,
      [VIEWS.LISTS]: this.els.lists.element,
      [VIEWS.LIST]: this.els.list.element,
    }

    Object.entries(panels).forEach(([view, panel]) => {
      panel.hidden = view !== this.activeView
    })
  }

  updateViewTriggers () {
    const triggers = this.querySelectorAll('[data-swish-wishlist-view]')
    const activeToolbarView =
      this.activeView === VIEWS.LIST ? VIEWS.LISTS : this.activeView

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.swishWishlistView === activeToolbarView

      trigger.dataset.active = String(isActive)

      if (isActive) {
        trigger.setAttribute('aria-current', 'page')
      } else {
        trigger.removeAttribute('aria-current')
      }
    })
  }

  updateListView () {
    if (!this.activeList) return

    const { name, itemCount } = this.activeList
    const countTranslation =
      itemCount === 1
        ? this.strings.savedCountOne
        : this.strings.savedCountOther

    this.els.listTitle.element.textContent = name
    this.els.listCount.element.textContent = translateString(countTranslation, {
      count: itemCount,
    })
  }

  async loadView (view) {
    if (view === VIEWS.HOME) {
      this.els.homeListsViewAll.element.hidden = true

      const [, , lists] = await Promise.all([
        this.loadSavedFromCart(),
        this.loadSavedItems(this.els.homeSaves.element, {
          limit: 6,
          carousel: true,
          itemActions: [ITEM_ACTIONS.ADD_TO_LIST],
          removeScope: REMOVE_SCOPES.WISHLIST,
        }),
        this.loadLists(this.els.homeLists.element, {
          limit: 4,
          renderLimit: 3,
        }),
      ])

      this.els.homeListsViewAll.element.hidden = lists.length <= 3

      return
    }

    if (view === VIEWS.SAVES) {
      await this.loadSavedItems(this.els.savesContent.element, {
        limit: 24,
        itemActions: [ITEM_ACTIONS.ADD_TO_LIST],
        removeScope: REMOVE_SCOPES.WISHLIST,
      })

      return
    }

    if (view === VIEWS.LISTS) {
      await this.loadLists(this.els.listsContent.element, {
        limit: 50,
        allowDelete: true,
      })

      return
    }

    if (view === VIEWS.LIST && this.activeList?.id) {
      await this.loadSavedItems(this.els.listContent.element, {
        limit: 24,
        listId: this.activeList.id,
        removeScope: REMOVE_SCOPES.LIST,
        emptyMessage: this.strings.emptyList,
      })
    }
  }

  setLoading (isLoading) {
    this.setAttribute('aria-busy', String(isLoading))
  }

  /*
  |--------------------------------------------------------------------------
  | Wishlist triggers
  |--------------------------------------------------------------------------
  |
  */
  setupWishlistTriggers (triggers = this.getExternalElements('trigger')) {
    Array.from(triggers).forEach((trigger) => {
      if (trigger.dataset.swishWishlistAction === 'save-from-cart') return

      this.setupWishlistTrigger(trigger)
    })
  }

  setupWishlistTriggersWithin (container) {
    if (!container) return

    this.setupWishlistTriggers(
      container.querySelectorAll(EXTERNAL_SELECTORS.TRIGGER),
    )
  }

  destroyWishlistTriggersWithin (container) {
    if (!container) return

    const triggers = container.querySelectorAll(EXTERNAL_SELECTORS.TRIGGER)

    triggers.forEach((trigger) => {
      const triggerState = this.wishlistTriggerStates.get(trigger)

      if (!triggerState) return

      triggerState.disposeEffect()
      triggerState.state.dispose()

      this.wishlistTriggerStates.delete(trigger)
    })
  }

  setupWishlistTrigger (trigger) {
    if (this.wishlistTriggerStates.has(trigger)) return

    if (!trigger.dataset.productId) {
      console.error(
        'Swish Wishlist: A wishlist trigger is missing a product ID.',
        trigger,
      )

      trigger.dataset.error = 'true'

      return
    }

    const context = this.swish.state.itemContext(trigger)

    const state = this.swish.state.itemState(context, {
      match: trigger.dataset.swishWishlistMatch || 'auto',
    })

    const disposeEffect = this.swish.state.effect(() => {
      this.renderWishlistTrigger(trigger, state.value)
    })

    this.wishlistTriggerStates.set(trigger, {
      state,
      disposeEffect,
    })
  }

  async handleWishlistTriggerClick (event) {
    const trigger = event.target.closest(EXTERNAL_SELECTORS.TRIGGER)

    if (!trigger || !this.swish) return

    const action = trigger.dataset.swishWishlistAction || 'toggle'

    event.preventDefault()
    event.stopPropagation()

    try {
      if (action === 'save-from-cart') {
        await this.saveFromCart(trigger)

        return
      }

      let triggerState = this.wishlistTriggerStates.get(trigger)

      if (!triggerState) {
        this.setupWishlistTrigger(trigger)

        triggerState = this.wishlistTriggerStates.get(trigger)
      }

      if (!triggerState || triggerState.state.value.loading) return

      await triggerState.state.toggle()
    } catch (error) {
      console.error('Swish Wishlist: Unable to update the saved item.', error)
    }
  }

  async saveFromCart (trigger) {
    const productId = Number(trigger.dataset.productId)
    const variantId = Number(trigger.dataset.variantId)
    const quantity = Number(trigger.dataset.quantity || 1)

    if (!productId || !variantId) {
      throw new Error(
        'Product or variant ID is missing from the cart save trigger.',
      )
    }

    trigger.disabled = true
    trigger.dataset.loading = 'true'

    try {
      const activity = await this.swish.intents.invoke('save:cart-line', {
        items: [
          {
            productId,
            variantId,
            quantity,
          },
        ],
      })

      const result = await activity.complete

      if (result?.code && result.code !== 'ok') {
        throw new Error(
          `Swish Save From Cart failed with code: ${result.code}`,
        )
      }
    } finally {
      if (trigger.isConnected) {
        trigger.disabled = false
        trigger.dataset.loading = 'false'
      }
    }
  }

  renderWishlistTrigger (trigger, state) {
    const label = trigger.querySelector('[data-swish-wishlist-label]')

    const labelText = state.saved
      ? trigger.dataset.swishWishlistRemoveLabel || this.strings.remove
      : trigger.dataset.swishWishlistSaveLabel || this.strings.save

    trigger.dataset.state = state.status
    trigger.dataset.saved = String(state.saved)
    trigger.dataset.loading = String(state.loading)

    trigger.toggleAttribute('data-error', Boolean(state.error))

    if (state.savedItemId) {
      trigger.dataset.swishItemId = state.savedItemId
    } else {
      delete trigger.dataset.swishItemId
    }

    trigger.disabled = state.loading

    trigger.setAttribute('aria-pressed', String(state.saved))
    trigger.setAttribute('aria-label', labelText)

    if (label) {
      label.textContent = labelText
    }
  }

  async handleAddToListClick (trigger) {
    const toast = this.toast
    const savedItem = trigger.closest(SELECTORS.ITEM)
    let itemId =
      trigger.dataset.swishItemId ||
      trigger.closest('[data-swish-item-id]')?.dataset.swishItemId

    if (!toast || !itemId || trigger.dataset.loading === 'true') return

    trigger.disabled = true
    trigger.dataset.loading = 'true'

    try {
      if (savedItem?.dataset.swishItemSource === SAVED_FROM_CART_LIST) {
        itemId = await this.promoteSavedFromCartItem(savedItem)
      }

      const { lists, item } = await this.loadListsForItem(itemId)
      const product = this.getSavedItemProduct(trigger)

      this.returnDrawerView = this.activeView

      if (lists.length === 0) {
        toast.showCreate({
          itemId,
          returnToWishlist: true,
          product,
        })
      } else {
        toast.showLists({
          lists,
          selectedListIds: item?.listIds || [],
          itemId,
          returnToWishlist: true,
          product,
        })
      }

      this.siteDrawers.open(TOAST_DRAWER_KEY)
    } catch (error) {
      console.error(
        'Swish Wishlist: Unable to load lists for the saved item.',
        error,
      )
    } finally {
      if (trigger.isConnected) {
        trigger.disabled = false
        trigger.dataset.loading = 'false'
      }
    }
  }

  /*
  | Items saved from the cart live in their own smart list and can't join
  | lists. Turn one into a regular save (Swish returns the existing save if
  | the product is already saved) and take it out of "saved from cart".
  */
  async promoteSavedFromCartItem (savedItem) {
    const { swishItemId, productId, variantId, quantity } = savedItem.dataset

    const createResponse = await this.swish.api.items.create({
      productId: Number(productId),
      variantId: Number(variantId),
      quantity: Number(quantity || 1),
    })

    const item = this.getResponseData(
      createResponse,
      'Unable to save the item to the wishlist.',
    )

    try {
      await this.removeSavedFromCartItem(swishItemId)
    } catch (error) {
      console.error(
        'Swish Wishlist: Unable to clear the item from saved from cart.',
        error,
      )
    }

    return item.id
  }

  getSavedItemProduct (trigger) {
    const savedItem = trigger.closest(SELECTORS.ITEM)
    const title = savedItem?.querySelector('.prd-Card_Title')
    const image = savedItem?.querySelector('.prd-Card_Image img')

    return {
      title: title?.textContent.trim() || '',
      image: image?.currentSrc || image?.src || '',
    }
  }

  async loadListsForItem (itemId) {
    const [listsResponse, itemResponse] = await Promise.all([
      this.swish.api.lists.list({
        limit: 50,
        sort: 'recently_updated',
      }),
      this.swish.api.items.findById(itemId),
    ])

    return {
      lists: this.getResponseData(
        listsResponse,
        'Unable to load wishlist lists.',
      ),
      item: this.getResponseData(
        itemResponse,
        'Unable to load the saved item.',
      ),
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Wishlist toast
  |--------------------------------------------------------------------------
  |
  */
  async handleToastAddToList (event) {
    const toast = this.toast
    const itemId = event.detail?.itemId

    if (!toast || !itemId) return

    toast.setLoading(true)

    try {
      const { lists, item } = await this.loadListsForItem(itemId)

      if (lists.length === 0) {
        toast.showCreate()

        return
      }

      toast.showLists({
        lists,
        selectedListIds: item?.listIds || [],
      })
    } catch (error) {
      console.error(
        'Swish Wishlist: Unable to load lists for the toast.',
        error,
      )

      toast.showError(this.strings.toastError)
    } finally {
      toast.setLoading(false)
    }
  }

  handleToastCreateList () {
    this.toast?.showCreate()
  }

  handleToastReturnToWishlist () {
    this.pendingDrawerView = this.returnDrawerView || VIEWS.HOME
    this.returnDrawerView = VIEWS.HOME

    this.siteDrawers.open(DRAWER_KEY)
  }

  handleToastView (event) {
    const lists = event.detail?.lists || []
    const list = lists.length === 1 ? lists[0] : null

    if (list?.id) {
      this.activeList = {
        id: list.id,
        name: list.name,
        itemCount: Number(list.itemCount || 0),
      }

      this.updateListView()
      this.pendingDrawerView = VIEWS.LIST
    } else {
      this.activeList = null
      this.pendingDrawerView = VIEWS.LISTS
    }

    this.siteDrawers.open(DRAWER_KEY)
  }

  handleToastComplete () {
    if (this.toast?.returnToWishlist) {
      this.handleToastReturnToWishlist()

      return
    }

    const toastDrawer = this.toast?.closest('[is="site-drawer"]')

    if (!toastDrawer?.open) return

    this.siteDrawers.close()
  }

  async handleToastSaveLists (event) {
    const toast = this.toast
    const itemId = event.detail?.itemId
    const selectedLists = event.detail?.lists || []
    const deselectedLists = event.detail?.removedLists || []

    if (!toast || !itemId) return
    if (selectedLists.length === 0 && deselectedLists.length === 0) return

    toast.setLoading(true)

    try {
      const itemResponse = await this.swish.api.items.findById(itemId)
      const item = this.getResponseData(
        itemResponse,
        'Unable to load the saved item.',
      )

      const currentListIds = item?.listIds || []
      const listsToAdd = selectedLists.filter(
        (list) => !currentListIds.includes(list.id),
      )
      const listsToRemove = deselectedLists.filter((list) =>
        currentListIds.includes(list.id),
      )

      await Promise.all([
        ...listsToAdd.map(async (list) => {
          const response = await this.swish.api.lists.addItemsToList(list.id, [
            itemId,
          ])

          this.getResponseData(
            response,
            `Unable to add the saved item to ${list.name}.`,
          )
        }),
        ...listsToRemove.map(async (list) => {
          const response = await this.swish.api.lists.removeItemFromList(
            list.id,
            itemId,
          )

          this.getResponseData(
            response,
            `Unable to remove the saved item from ${list.name}.`,
          )
        }),
      ])

      toast.showAdded({
        lists: selectedLists.map((list) => {
          return {
            ...list,
            itemCount: Number(list.itemCount || 0) + 1,
          }
        }),
        removedLists: deselectedLists.map((list) => {
          return {
            ...list,
            itemCount: Math.max(0, Number(list.itemCount || 0) - 1),
          }
        }),
      })
    } catch (error) {
      console.error('Swish Wishlist: Unable to update the item lists.', error)

      toast.showError(this.strings.toastError)
    } finally {
      toast.setLoading(false)
    }
  }

  async handleToastSaveCreate (event) {
    const toast = this.toast
    const itemId = event.detail?.itemId
    const name = event.detail?.name

    if (!toast || !name) return

    toast.setLoading(true)

    try {
      const createResponse = await this.swish.api.lists.create({
        name,
      })

      const list = this.getResponseData(
        createResponse,
        'Unable to create the wishlist list.',
      )

      if (!itemId) {
        this.activeList = {
          id: list.id,
          name: list.name,
          itemCount: Number(list.itemCount || 0),
        }

        this.updateListView()

        this.pendingDrawerView = VIEWS.LIST
        this.siteDrawers.open(DRAWER_KEY)

        return
      }

      const addResponse = await this.swish.api.lists.addItemsToList(list.id, [
        itemId,
      ])

      this.getResponseData(
        addResponse,
        'Unable to add the saved item to the new list.',
      )

      toast.showAdded({
        lists: [
          {
            id: list.id,
            name: list.name,
            itemCount: Number(list.itemCount || 0) + 1,
          },
        ],
      })
    } catch (error) {
      console.error('Swish Wishlist: Unable to create the list.', error)

      toast.showError(this.strings.toastError)
    } finally {
      toast.setLoading(false)
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Wishlist count
  |--------------------------------------------------------------------------
  |
  */
  setupWishlistCount () {
    const savedCountElements = this.els.savedCount.elements
    const countState = this.swish.state.itemCount()

    this.disposeCountEffect = this.swish.state.effect(() => {
      const { count, loading, error } = countState.value
      const indicators = this.getExternalElements('indicator')

      indicators.forEach((indicator) => {
        indicator.setAttribute('aria-hidden', String(loading || count === 0))
        indicator.dataset.loading = String(loading)
        indicator.dataset.count = String(count)
        indicator.toggleAttribute('data-error', Boolean(error))
      })

      savedCountElements.forEach((savedCountElement) => {
        const countTranslation =
          count === 1
            ? this.strings.savedCountOne
            : this.strings.savedCountOther

        savedCountElement.textContent = loading
          ? ''
          : translateString(countTranslation, { count })

        savedCountElement.toggleAttribute('data-error', Boolean(error))
      })
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Swish events
  |--------------------------------------------------------------------------
  |
  */
  setupSwishEventListeners () {
    this.unsubscribeSwishEvents = this.swish.events.subscribe(
      SWISH_EVENTS,
      this.handleSwishChanged.bind(this),
    )
  }

  handleSwishChanged () {
    if (!this.drawerIsOpen) return
    if (Date.now() < this.ignoreSwishChangesUntil) return

    this.showView(this.activeView)
  }

  /*
  |--------------------------------------------------------------------------
  | Saved from cart
  |--------------------------------------------------------------------------
  |
  */
  async loadSavedFromCart () {
    const response = await this.swish.api.smartLists.findItems(
      SAVED_FROM_CART_LIST,
      {
        limit: 6,
      },
    )

    const items = this.getResponseData(
      response,
      'Unable to load items saved from cart.',
    )

    const hasItems = await this.renderSavedItemRecords(
      this.els.savedFromCart.element,
      items,
      {
        carousel: true,
        showEmptyState: false,
        itemActions: [ITEM_ACTIONS.ADD_TO_LIST],
        removeScope: REMOVE_SCOPES.SAVED_FROM_CART,
      },
    )

    this.els.savedFromCartSection.element.hidden = !hasItems

    if (hasItems) {
      this.refreshCarouselsWithin(this.els.savedFromCart.element)
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Saved items
  |--------------------------------------------------------------------------
  |
  */
  async loadSavedItems (
    container,
    {
      limit,
      listId,
      carousel = false,
      itemActions = [],
      removeScope,
      emptyMessage,
    },
  ) {
    const response = await this.swish.api.items.list({
      limit,
      listId,
      sort: 'recently_saved',
    })

    const items = this.getResponseData(response, 'Unable to load saved items.')

    return this.renderSavedItemRecords(container, items, {
      carousel,
      showEmptyState: true,
      itemActions,
      removeScope,
      emptyMessage,
    })
  }

  async renderSavedItemRecords (
    container,
    items,
    {
      carousel = false,
      showEmptyState = true,
      itemActions = [],
      removeScope,
      emptyMessage = this.strings.emptySavedItems,
    },
  ) {
    if (items.length === 0) {
      this.destroyWishlistTriggersWithin(container)

      if (showEmptyState) {
        this.renderEmpty(container, emptyMessage)
      } else {
        container.replaceChildren()
      }

      return false
    }

    const records = await Promise.all(
      items.map(async (item) => {
        const { product } =
          await this.swish.storefront.loadResolvedProduct(item)

        return {
          item,
          product,
        }
      }),
    )

    return this.renderSavedItems(container, records, {
      carousel,
      showEmptyState,
      itemActions,
      removeScope,
    })
  }

  async renderSavedItems (
    container,
    records,
    { carousel, showEmptyState, itemActions, removeScope },
  ) {
    const validRecords = records.filter(({ product }) => product?.handle)

    if (validRecords.length === 0) {
      this.destroyWishlistTriggersWithin(container)

      if (showEmptyState) {
        this.renderEmpty(container, this.strings.emptySavedProducts)
      } else {
        container.replaceChildren()
      }

      return false
    }

    const productCards = await Promise.all(
      validRecords.map(async ({ item, product }) => {
        const productCardHtml = await this.getProductCardHtml(product.handle)

        if (!productCardHtml) return ''

        return this.getSavedProductCardHtml(productCardHtml, item, {
          itemActions,
          removeScope,
        })
      }),
    )

    const validProductCards = productCards.filter(Boolean)

    if (validProductCards.length === 0) {
      this.destroyWishlistTriggersWithin(container)

      if (showEmptyState) {
        this.renderEmpty(container, this.strings.emptySavedProducts)
      } else {
        container.replaceChildren()
      }

      return false
    }

    this.destroyWishlistTriggersWithin(container)

    container.innerHTML = carousel
      ? this.getCarouselHtml(validProductCards)
      : validProductCards.join('')

    this.setupWishlistTriggersWithin(container)

    return true
  }

  getSavedProductCardHtml (
    productCardHtml,
    item,
    { itemActions = [], removeScope } = {},
  ) {
    const template = document.createElement('template')

    template.innerHTML = productCardHtml.trim()

    const productCard = template.content.firstElementChild

    if (!productCard) return ''

    if (item?.id) {
      productCard.dataset.swishItemId = item.id
    }

    if (!item?.id || (itemActions.length === 0 && !removeScope)) {
      return productCard.outerHTML
    }

    if (removeScope) {
      this.replaceHeartWithRemove(productCard, item.id, removeScope)
    }

    const savedItem = this.createSavedItem(productCard, item.id, itemActions)

    if (removeScope === REMOVE_SCOPES.SAVED_FROM_CART) {
      savedItem.dataset.swishItemSource = SAVED_FROM_CART_LIST
      savedItem.dataset.productId = item.productId
      savedItem.dataset.variantId = item.variantId
      savedItem.dataset.quantity = item.quantity || 1
    }

    return savedItem.outerHTML
  }

  createSavedItem (productCard, itemId, itemActions) {
    const savedItem = document.createElement('div')

    savedItem.className = 'drw-Wishlist_Item'
    savedItem.dataset.swishWishlistItem = ''
    savedItem.dataset.swishItemId = itemId

    savedItem.append(productCard)

    if (itemActions.length === 0) return savedItem

    const actions = document.createElement('div')
    const titleId = this.getCardTitleId(productCard)

    actions.className = 'drw-Wishlist_ItemActions'

    itemActions.forEach((itemAction) => {
      const button = this.createItemActionButton(itemAction, itemId)

      if (!button) return

      if (titleId) {
        button.setAttribute('aria-describedby', titleId)
      }

      actions.append(button)
    })

    savedItem.append(actions)

    return savedItem
  }

  createItemActionButton (itemAction, itemId) {
    if (itemAction !== ITEM_ACTIONS.ADD_TO_LIST) return null

    const button = document.createElement('button')

    button.className = 'drw-Wishlist_ItemAction btn-Button btn-Button-link'
    button.type = 'button'
    button.dataset.swishWishlistAddToList = ''
    button.dataset.swishItemId = itemId
    button.textContent = this.strings.addToList

    return button
  }

  /*
  |--------------------------------------------------------------------------
  | Remove (X)
  |--------------------------------------------------------------------------
  | Everything in the drawer is already saved, so the card's heart is swapped
  | for an X. Inside a list it removes the item from that list only;
  | everywhere else it removes the save (and Swish drops it from every list).
  |
  */
  replaceHeartWithRemove (productCard, itemId, scope) {
    const heart = productCard.querySelector(SELECTORS.CARD_HEART)

    if (!heart) return

    const button = document.createElement('button')
    const titleId = this.getCardTitleId(productCard)

    button.className = 'prd-Card_MediaButton drw-Wishlist_Remove'
    button.type = 'button'
    button.dataset.swishWishlistRemove = scope
    button.dataset.swishItemId = itemId
    button.setAttribute('aria-label', this.getRemoveLabel(scope))

    if (titleId) {
      button.setAttribute('aria-describedby', titleId)
    }

    button.append(this.els.removeIcon.element.content.cloneNode(true))

    heart.replaceWith(button)
  }

  getRemoveLabel (scope) {
    if (scope === REMOVE_SCOPES.LIST && this.activeList?.name) {
      return translateString(this.strings.removeFromList, {
        list: this.activeList.name,
      })
    }

    if (scope === REMOVE_SCOPES.SAVED_FROM_CART) {
      return translateString(this.strings.removeFromList, {
        list: this.strings.savedFromCart,
      })
    }

    return this.strings.remove
  }

  getCardTitleId (productCard) {
    return productCard.querySelector('.prd-Card_Title')?.id || ''
  }

  async handleRemoveClick (trigger) {
    const savedItem = trigger.closest(SELECTORS.ITEM)
    const itemId = trigger.dataset.swishItemId

    if (!savedItem || !itemId || trigger.dataset.loading === 'true') return

    this.hideAllRemoveConfirms()
    this.clearItemError(savedItem)

    const scope = trigger.dataset.swishWishlistRemove

    if (scope === REMOVE_SCOPES.LIST) {
      await this.removeSavedItemWith(savedItem, () => {
        return this.removeItemFromActiveList(itemId)
      })

      return
    }

    if (scope === REMOVE_SCOPES.SAVED_FROM_CART) {
      await this.removeSavedItemWith(savedItem, () => {
        return this.removeSavedFromCartItem(itemId)
      })

      return
    }

    let listCount = 0

    this.setTriggerLoading(trigger, true)

    try {
      const response = await this.swish.api.items.findById(itemId)
      const item = this.getResponseData(
        response,
        'Unable to load the saved item.',
      )

      listCount = item?.listIds?.length || 0
    } catch (error) {
      console.error('Swish Wishlist: Unable to check the item lists.', error)

      this.renderItemError(savedItem, this.strings.removeError)

      return
    } finally {
      this.setTriggerLoading(trigger, false)
    }

    if (listCount > 0) {
      this.showRemoveConfirm(savedItem, listCount)

      return
    }

    await this.removeSavedItemWith(savedItem, () => this.deleteItem(itemId))
  }

  async handleRemoveConfirm (trigger) {
    const savedItem = trigger.closest(SELECTORS.ITEM)
    const itemId = savedItem?.dataset.swishItemId

    if (!savedItem || !itemId) return

    this.hideRemoveConfirm(savedItem)

    await this.removeSavedItemWith(savedItem, () => this.deleteItem(itemId))
  }

  async removeSavedItemWith (savedItem, removeAction) {
    savedItem.dataset.removing = 'true'

    this.ignoreSwishChangesUntil = Infinity

    try {
      await removeAction()

      this.removeSavedItem(savedItem)
    } catch (error) {
      console.error('Swish Wishlist: Unable to remove the saved item.', error)

      savedItem.removeAttribute('data-removing')

      this.renderItemError(savedItem, this.strings.removeError)
    } finally {
      this.ignoreSwishChangesUntil = Date.now() + OWN_CHANGE_GRACE_PERIOD
    }
  }

  async removeItemFromActiveList (itemId) {
    const listId = this.activeList?.id

    if (!listId) {
      throw new Error('There is no open list to remove the item from.')
    }

    const response = await this.swish.api.lists.removeItemFromList(
      listId,
      itemId,
    )

    this.getResponseData(
      response,
      'Unable to remove the saved item from the list.',
    )

    if (this.activeList?.id === listId) {
      this.activeList.itemCount = Math.max(0, this.activeList.itemCount - 1)
      this.updateListView()
    }
  }

  async deleteItem (itemId) {
    const response = await this.swish.api.items.deleteById(itemId)

    this.getResponseData(response, 'Unable to remove the saved item.')
  }

  async removeSavedFromCartItem (itemId) {
    const response = await this.swish.api.smartLists.removeItem(
      SAVED_FROM_CART_LIST,
      itemId,
    )

    this.getResponseData(
      response,
      'Unable to remove the item saved from cart.',
    )
  }

  removeSavedItem (savedItem) {
    if (!savedItem?.isConnected) return

    /* Carousels need rebuilding rather than a slide pulling out of them */
    if (savedItem.closest('css-carousel')) {
      this.showView(this.activeView)

      return
    }

    const container = savedItem.parentElement
    const items = Array.from(container.querySelectorAll(SELECTORS.ITEM))
    const index = items.indexOf(savedItem)

    this.destroyWishlistTriggersWithin(savedItem)

    savedItem.remove()

    const remainingItems = items.filter((item) => item !== savedItem)

    if (remainingItems.length === 0) {
      const emptyMessage =
        container === this.els.listContent.element
          ? this.strings.emptyList
          : this.strings.emptySavedItems

      this.renderEmpty(container, emptyMessage)

      return
    }

    const nextItem = remainingItems[Math.min(index, remainingItems.length - 1)]

    nextItem.querySelector(SELECTORS.REMOVE)?.focus()
  }

  showRemoveConfirm (savedItem, listCount) {
    const confirm = document.createElement('div')
    const text = document.createElement('p')
    const actions = document.createElement('div')
    const submit = document.createElement('button')
    const cancel = document.createElement('button')

    confirm.className = 'drw-Wishlist_ItemConfirm'
    confirm.setAttribute('role', 'group')
    confirm.setAttribute('aria-label', this.strings.remove)

    text.className = 'drw-Wishlist_ItemConfirmText fz-13_150'
    text.textContent = translateString(
      listCount === 1
        ? this.strings.removeConfirmOne
        : this.strings.removeConfirmOther,
      { count: listCount },
    )

    actions.className = 'drw-Wishlist_ItemConfirmActions'

    submit.className =
      'drw-Wishlist_ItemConfirmSubmit btn-Button btn-Button-primary'
    submit.type = 'button'
    submit.dataset.swishWishlistRemoveConfirm = ''
    submit.textContent = this.strings.removeConfirm

    cancel.className =
      'drw-Wishlist_ItemConfirmCancel btn-Button btn-Button-link'
    cancel.type = 'button'
    cancel.dataset.swishWishlistRemoveCancel = ''
    cancel.textContent = this.strings.cancel

    actions.append(submit, cancel)
    confirm.append(text, actions)

    savedItem.dataset.confirming = 'true'
    savedItem.append(confirm)

    cancel.focus()
  }

  hideRemoveConfirm (savedItem, { restoreFocus = false } = {}) {
    if (!savedItem) return

    savedItem.querySelector('.drw-Wishlist_ItemConfirm')?.remove()
    savedItem.removeAttribute('data-confirming')

    if (restoreFocus) {
      savedItem.querySelector(SELECTORS.REMOVE)?.focus()
    }
  }

  hideAllRemoveConfirms () {
    this.querySelectorAll(`${SELECTORS.ITEM}[data-confirming]`).forEach(
      (savedItem) => this.hideRemoveConfirm(savedItem),
    )
  }

  setTriggerLoading (trigger, isLoading) {
    trigger.disabled = isLoading
    trigger.dataset.loading = String(isLoading)
  }

  renderItemError (savedItem, text) {
    if (!savedItem?.isConnected || !text) return

    this.clearItemError(savedItem)

    const message = document.createElement('p')

    message.className = 'drw-Wishlist_ItemError fz-12_150'
    message.setAttribute('role', 'alert')
    message.textContent = text

    savedItem.append(message)
  }

  clearItemError (savedItem) {
    savedItem?.querySelector('.drw-Wishlist_ItemError')?.remove()
  }

  /*
  |--------------------------------------------------------------------------
  | Carousels
  |--------------------------------------------------------------------------
  |
  */
  refreshCarouselsWithin (container) {
    const carousels = container.querySelectorAll('css-carousel')

    carousels.forEach((carousel) => {
      carousel.dispatchEvent(new CustomEvent('BAO::CSS_CAROUSEL::VISIBLE'))
    })
  }

  getCarouselHtml (productCards) {
    const slides = productCards
      .map((productCard) => {
        return `
          <div
            class="drw-Wishlist_Slide keen-slider__slide"
            data-css-carousel-el="slide"
          >
            ${productCard}
          </div>
        `
      })
      .join('')

    return `
      <css-carousel
        class="drw-Wishlist_Carousel"
        data-spacing="2"
        style="--Card_Spacing: 2px;"
      >
        <div
          class="drw-Wishlist_Slides keen-slider"
          data-css-carousel-el="slides"
        >
          ${slides}
        </div>
      </css-carousel>
    `
  }

  async getProductCardHtml (productHandle) {
    const cachedProductCard = this.productCardCache.get(productHandle)

    if (cachedProductCard) {
      return cachedProductCard
    }

    const productUrl = [
      window.Shopify?.routes?.root || '/',
      'products/',
      encodeURIComponent(productHandle),
    ].join('')

    try {
      const sectionHtml = await sectionRenderer.fetchSingle(
        PRODUCT_CARD_SECTION,
        productUrl,
      )

      const sectionDocument = sectionRenderer.convertToDOM(sectionHtml)
      const productCardElement = sectionDocument.querySelector(
        PRODUCT_CARD_SELECTOR,
      )

      if (!productCardElement) {
        console.error(
          `Swish Wishlist: No product card was returned for ${productHandle}.`,
        )

        return ''
      }

      const productCardHtml = productCardElement.outerHTML

      this.productCardCache.set(productHandle, productCardHtml)

      return productCardHtml
    } catch (error) {
      console.error(
        `Swish Wishlist: Failed to fetch product card for ${productHandle}.`,
        error,
      )

      return ''
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Lists
  |--------------------------------------------------------------------------
  |
  */
  async loadLists (
    container,
    { limit, renderLimit = limit, allowDelete = false },
  ) {
    const response = await this.swish.api.lists.list({
      limit,
      sort: 'recently_updated',
    })

    const lists = this.getResponseData(response, 'Unable to load lists.')

    if (lists.length === 0) {
      this.renderEmpty(container, this.strings.emptyLists)

      return lists
    }

    this.renderLists(container, lists.slice(0, renderLimit), { allowDelete })

    return lists
  }

  renderLists (container, lists, { allowDelete = false } = {}) {
    const fragment = document.createDocumentFragment()

    lists.forEach((list) => {
      const item =
        this.els.listTemplate.element.content.firstElementChild.cloneNode(true)

      this.populateListItem(item, list, { allowDelete })

      fragment.append(item)
    })

    container.replaceChildren(fragment)
  }

  populateListItem (item, list, { allowDelete = false } = {}) {
    const link = item.querySelector('[data-swish-list-el="link"]')
    const image = item.querySelector('[data-swish-list-el="image"]')
    const name = item.querySelector('[data-swish-list-el="name"]')
    const count = item.querySelector('[data-swish-list-el="count"]')
    const deleteTrigger = item.querySelector('[data-swish-list-el="delete"]')
    const deleteConfirm = item.querySelector(
      '[data-swish-list-el="deleteConfirm"]',
    )
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const previewImage = list.previewImages?.[0]
    const itemCount = list.itemCount || 0

    item.dataset.swishListId = list.id
    item.dataset.swishListName = list.name

    link.dataset.swishWishlistListId = list.id
    link.dataset.swishWishlistListName = list.name
    link.dataset.swishWishlistListCount = String(itemCount)

    const countTranslation =
      itemCount === 1 ? this.strings.listCountOne : this.strings.listCountOther

    name.textContent = list.name
    count.textContent = translateString(countTranslation, {
      count: itemCount,
    })

    if (previewImage?.url) {
      image.src = previewImage.url
      image.hidden = false
    }

    if (!deleteTrigger || !deleteConfirm || !deleteText) return

    deleteTrigger.hidden = !allowDelete
    deleteTrigger.setAttribute(
      'aria-label',
      translateString(this.strings.listDeleteLabel, {
        list: list.name,
      }),
    )
    deleteTrigger.setAttribute('aria-expanded', 'false')

    deleteText.textContent = translateString(this.strings.listDeleteConfirm, {
      list: list.name,
    })
  }

  handleListAction (trigger) {
    const item = trigger.closest('[data-swish-list-el="item"]')

    if (!item) return

    const action = trigger.dataset.swishListAction

    if (action === 'delete') {
      this.showListDeleteConfirm(item)

      return
    }

    if (action === 'cancel-delete') {
      this.hideListDeleteConfirm(item)

      return
    }

    if (action === 'confirm-delete') {
      this.deleteList(item)
    }
  }

  showListDeleteConfirm (item) {
    this.querySelectorAll('[data-delete-confirm="true"]').forEach(
      (listItem) => {
        if (listItem !== item) {
          this.hideListDeleteConfirm(listItem)
        }
      },
    )

    const deleteTrigger = item.querySelector('[data-swish-list-el="delete"]')
    const deleteConfirm = item.querySelector(
      '[data-swish-list-el="deleteConfirm"]',
    )
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const deleteError = item.querySelector(
      '[data-swish-list-el="deleteError"]',
    )
    const cancelTrigger = item.querySelector(
      '[data-swish-list-action="cancel-delete"]',
    )

    if (!deleteConfirm) return

    item.dataset.deleteConfirm = 'true'
    deleteConfirm.hidden = false
    deleteTrigger?.setAttribute('aria-expanded', 'true')

    if (deleteText) {
      deleteText.hidden = false
    }

    if (deleteError) {
      deleteError.textContent = ''
      deleteError.hidden = true
    }

    requestAnimationFrame(() => {
      cancelTrigger?.focus()
    })
  }

  hideListDeleteConfirm (item) {
    const deleteTrigger = item.querySelector('[data-swish-list-el="delete"]')
    const deleteConfirm = item.querySelector(
      '[data-swish-list-el="deleteConfirm"]',
    )
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const deleteError = item.querySelector(
      '[data-swish-list-el="deleteError"]',
    )

    delete item.dataset.deleteConfirm
    delete item.dataset.loading

    if (deleteConfirm) {
      deleteConfirm.hidden = true
    }

    deleteTrigger?.setAttribute('aria-expanded', 'false')

    if (deleteText) {
      deleteText.hidden = false
    }

    if (deleteError) {
      deleteError.textContent = ''
      deleteError.hidden = true
    }
  }

  async deleteList (item) {
    const listId = item.dataset.swishListId
    const listName = item.dataset.swishListName
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const deleteError = item.querySelector(
      '[data-swish-list-el="deleteError"]',
    )
    const actionTriggers = item.querySelectorAll('[data-swish-list-action]')
    const listApi = this.swish.api?.lists || this.swish.lists
    const deleteById = listApi?.deleteById

    if (!listId || item.dataset.loading === 'true') return

    if (typeof deleteById !== 'function') {
      console.error('Swish Wishlist: List deletion is unavailable.')

      if (deleteText) {
        deleteText.hidden = true
      }

      if (deleteError) {
        deleteError.textContent = this.strings.listDeleteError
        deleteError.hidden = false
      }

      return
    }

    item.dataset.loading = 'true'
    item.setAttribute('aria-busy', 'true')

    actionTriggers.forEach((actionTrigger) => {
      actionTrigger.disabled = true
    })

    if (deleteText) {
      deleteText.hidden = false
    }

    if (deleteError) {
      deleteError.textContent = ''
      deleteError.hidden = true
    }

    try {
      const response = await deleteById.call(listApi, listId)

      if (response?.error) {
        throw new Error(
          response.error?.message || `Unable to delete ${listName}.`,
        )
      }

      item.remove()

      if (
        !this.els.listsContent.element.querySelector(
          '[data-swish-list-el="item"]',
        )
      ) {
        this.renderEmpty(
          this.els.listsContent.element,
          this.strings.emptyLists,
        )
      }
    } catch (error) {
      console.error('Swish Wishlist: Unable to delete the list.', error)

      item.dataset.loading = 'false'
      item.setAttribute('aria-busy', 'false')

      actionTriggers.forEach((actionTrigger) => {
        actionTrigger.disabled = false
      })

      if (deleteText) {
        deleteText.hidden = true
      }

      if (deleteError) {
        deleteError.textContent = this.strings.listDeleteError
        deleteError.hidden = false
      }
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Swish
  |--------------------------------------------------------------------------
  |
  */
  async getSwish () {
    if (window.swish) {
      return window.swish
    }

    if (swishPromise) {
      return swishPromise
    }

    swishPromise = new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        document.removeEventListener(SWISH_READY_EVENT, handleReady)

        swishPromise = null

        reject(new Error('Swish did not initialise within 10 seconds.'))
      }, SWISH_READY_TIMEOUT)

      function handleReady () {
        window.clearTimeout(timeout)

        if (!window.swish) {
          swishPromise = null

          reject(
            new Error(
              'Swish fired its ready event but window.swish is unavailable.',
            ),
          )

          return
        }

        resolve(window.swish)
      }

      document.addEventListener(SWISH_READY_EVENT, handleReady, {
        once: true,
      })
    })

    return swishPromise
  }

  getResponseData (response, fallbackMessage) {
    if ('error' in response) {
      throw new Error(response.error?.message || fallbackMessage)
    }

    return response.data ?? []
  }

  get toast () {
    return this.getExternalElements('toast')[0] || null
  }

  get strings () {
    return window.theme.strings.wishlist
  }

  /*
  |--------------------------------------------------------------------------
  | Elements
  |--------------------------------------------------------------------------
  |
  */
  getExternalElements (name) {
    const externalElement = this.externalEls[name]

    if (!externalElement) {
      return []
    }

    if (externalElement.elements) {
      return Array.from(externalElement.elements)
    }

    if (externalElement.element) {
      return [externalElement.element]
    }

    return []
  }

  /*
  |--------------------------------------------------------------------------
  | Empty and error states
  |--------------------------------------------------------------------------
  |
  */
  renderEmpty (container, text) {
    const message = document.createElement('p')

    message.className = 'drw-Drawer_Empty fz-15_150'
    message.textContent = text

    this.destroyWishlistTriggersWithin(container)

    container.replaceChildren(message)
  }

  renderError (container, text) {
    const message = document.createElement('p')

    message.className = 'drw-Drawer_Error'
    message.setAttribute('role', 'alert')
    message.textContent = text

    this.destroyWishlistTriggersWithin(container)

    container.replaceChildren(message)
  }

  renderViewError (view) {
    const containers = {
      [VIEWS.HOME]: this.els.homeSaves.element,
      [VIEWS.SAVES]: this.els.savesContent.element,
      [VIEWS.LISTS]: this.els.listsContent.element,
      [VIEWS.LIST]: this.els.listContent.element,
    }

    const container = containers[view]

    if (!container) return

    this.renderError(container, this.strings.errorView)
  }
}

if (!customElements.get('swish-wishlist')) {
  customElements.define('swish-wishlist', SwishWishlist)
}
